import Analytics from '@/utils/analytics';
import moment from 'moment-timezone';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertKeys,
  canDisplayItemPrices,
  EmployeeRestaurant,
  SearchSortOrder,
  UserContext,
  UserContextType,
  UserSettingsContext,
  UserSettingsContextType,
} from './user-context';
import { AuthService } from '@domain/auth/blocs/services/auth-service';
import {
  AuthStatus,
  ChatStatus,
  ListDisplayMode,
  PaymentsStatus,
  VerificationMethod,
} from '@/enums';
import { captureException, ErrorReporter } from '@/utils/sentry';
import { DynamicConfig } from './dynamic-config';
import { gql, useApolloClient } from '@apollo/client';
import { Hub } from 'aws-amplify';
import { INITIAL_SMART_ORDER_QUERY } from './smart-order-queries';
import {
  Initial_SmartOrderQuery,
  Initial_SmartOrderQueryVariables,
  SmartOrder_UserContextQuery,
  SmartOrder_UserContextQueryVariables,
} from '@/types';
import { ItemSortOrder } from './item-context-types';
import { normalizeQuotes } from '@/utils/string';
import { SessionService } from '@domain/auth/blocs/services/session-service';
import { SmartOrderParams } from '@/utils/smart-order';
import { USER_FRAGMENT } from './user-context-queries';
import { useSendChallengeAnswer } from '@/hooks/use-send-challenge-answer';
import { useSmartOrderRestaurant } from '@/hooks/use-smart-order-restaurant';
import { useWrappedQuery } from '@/hooks/use-wrapped-query';

export const SMART_ORDER_USER_CONTEXT_QUERY = gql`
  query SmartOrder_UserContext(
    $businessOrganizationUUID: uuid!
    $userUUID: uuid!
  ) {
    businesses(
      where: {
        type: { _eq: "SUPPLIER" }
        business_organization_uuid: { _eq: $businessOrganizationUUID }
      }
    ) {
      supplier_metadata {
        dynamic_config
        uuid
      }
      uuid
    }

    employees_by_pk(uuid: $userUUID) {
      ...UserContextUser
    }

    employee_chats(
      where: {
        business_organization_uuid: { _eq: $businessOrganizationUUID }
        smart_order_enabled: { _eq: true }
      }
    ) {
      chat_dynamic_config
      chat_uuid
      display_item_prices
      restaurant_account_id
      restaurant_address
      restaurant_name
      restaurant_timezone
      restaurant_uuid
      status
      supplier_uuid
    }

    whitelabel_config(
      where: { business_organization_uuid: { _eq: $businessOrganizationUUID } }
    ) {
      translations_enabled
      uuid
    }
  }

  ${USER_FRAGMENT}
`;

const SMART_ORDER_USER_SETTINGS_CONTEXT_VALUE: UserSettingsContextType = {
  alertsSeen: {
    [AlertKeys.OrderGuideButton]: false,
    [AlertKeys.SubscriptionButton]: false,
  },
  catalogSortOrder: ItemSortOrder.Alphabetical,
  inInboundTrainingMode: false,
  isDescriptionVisible: true,
  largeItemListDisplayMode: ListDisplayMode.List,
  searchSortOrder: SearchSortOrder.Alphabetical,
  setAlertSeen: () => {},
  setCatalogSortOrder: () => {},
  setInInboundTrainingMode: () => {},
  setIsDescriptionVisible: () => {},
  setLargeItemListDisplayMode: () => {},
  setSearchSortOrder: () => {},
  setShouldFlashlightActivate: () => {},
  setShowSalesRepUI: () => {},
  shouldFlashlightActivate: false,
  showSalesRepUI: false,
};

interface Props {
  children: React.ReactNode;
  smartOrderParams: SmartOrderParams;
}

// TODO: https://usepepper.atlassian.net/browse/EXP-1609
// should be able to select different bizorgs
export const SmartOrderUserContextProvider: React.FC<Props> = ({
  children,
  smartOrderParams,
}) => {
  const apolloClient = useApolloClient();
  const sendChallengeAnswer = useSendChallengeAnswer();

  const [authenticatedUserUUID, setAuthenticatedUserUUID] = useState<
    string | null
  >(null);

  const { data, loading } = useWrappedQuery<
    SmartOrder_UserContextQuery,
    SmartOrder_UserContextQueryVariables
  >(SMART_ORDER_USER_CONTEXT_QUERY, {
    fetchPolicy: 'network-only',
    skip: !authenticatedUserUUID || !smartOrderParams.businessOrganizationUUID,
    variables: {
      businessOrganizationUUID: smartOrderParams.businessOrganizationUUID!,
      userUUID: authenticatedUserUUID!,
    },
  });

  const { data: smartOrderData, loading: smartOrderLoading } = useWrappedQuery<
    Initial_SmartOrderQuery,
    Initial_SmartOrderQueryVariables
  >(INITIAL_SMART_ORDER_QUERY, {
    fetchPolicy: 'network-only',
    skip: !authenticatedUserUUID || !smartOrderParams.smartOrderUUID,
    variables: {
      smartOrderUUID: smartOrderParams.smartOrderUUID!,
    },
  });

  const { onSelectRestaurant, regenerateSmartOrdersLoading } =
    useSmartOrderRestaurant({
      smartOrderParams,
    });

  const checkAuthStatus = useCallback(async () => {
    try {
      const userUUID = await SessionService.getEmployeeUUID();
      setAuthenticatedUserUUID(userUUID);

      // Identify Sentry and analytics user
      ErrorReporter.setUser({ id: userUUID });
      Analytics.load();
      Analytics.identify(userUUID);
    } catch (e) {
      setAuthenticatedUserUUID(null);

      // Clear Sentry and analytics user
      ErrorReporter.setUser(null);
      Analytics.reset();

      // resetStore refetches queries and causes errors since there's no longer an
      // authenticated user so setImmediate gives the queries time to unmount first
      setImmediate(() => {
        apolloClient.resetStore();
      });
    }
  }, [apolloClient]);

  // Register auth event listeners
  useEffect(() => {
    Hub.listen('auth', checkAuthStatus);
    return () => {
      Hub.remove('auth', checkAuthStatus);
    };
  }, [checkAuthStatus]);

  useEffect(() => {
    (async () => {
      try {
        const user = await AuthService.signIn(smartOrderParams.username);
        const autoLoginPayload = JSON.stringify({
          business_organization_id: smartOrderParams.businessOrganizationUUID,
          buyer_cookie: smartOrderParams.smartOrderUUID,
        });
        await sendChallengeAnswer(
          user,
          autoLoginPayload,
          VerificationMethod.Punchout,
        );
        // We can check auth status here, without waiting for Hub events
        void checkAuthStatus();
      } catch (e) {
        captureException(e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [smartOrderParams]);

  const value = useMemo<UserContextType | null>(() => {
    const user = data?.employees_by_pk;
    const smartOrder = smartOrderData?.smart_order_by_pk;
    if (
      loading ||
      !authenticatedUserUUID ||
      !user ||
      !smartOrder ||
      smartOrderLoading ||
      regenerateSmartOrdersLoading
    ) {
      // Return null to show the loading state
      return null;
    }

    const supplierDynamicConfigMap = data?.businesses.reduce<{
      [supplierUUID: string]: DynamicConfig;
    }>((result, business) => {
      result[business.uuid] = business.supplier_metadata?.dynamic_config || {};
      return result;
    }, {});

    const employeeChats = data?.employee_chats;
    const restaurantRelationships: EmployeeRestaurant[] = [];
    const restaurantUUIDToRelationshipMap: Record<string, EmployeeRestaurant> =
      {};
    let selectedRestaurantRelationship: EmployeeRestaurant | null = null;
    for (const ec of employeeChats) {
      const chatDynamicConfig: DynamicConfig = ec.chat_dynamic_config;
      const supplierDynamicConfig = supplierDynamicConfigMap[ec.supplier_uuid!];
      const restaurantRelationship: EmployeeRestaurant = {
        accountID:
          typeof ec.restaurant_account_id === 'string'
            ? normalizeQuotes(ec.restaurant_account_id)
            : null,
        addressString: normalizeQuotes(ec.restaurant_address!),
        appPaymentsEnabled: false,
        carouselHoldout: false,
        categoryImagesEnabled: false,
        chatDynamicConfig,
        chatStatus: ec.status as ChatStatus,
        chatUUIDs: [ec.chat_uuid!],
        commissionSettings: null,
        consumptionTax: null,
        countryCode: null,
        customerCostType: null,
        displayItemPrices: canDisplayItemPrices(
          ec.display_item_prices!,
          ec.status as ChatStatus,
          chatDynamicConfig,
          supplierDynamicConfig,
          false, // it's assumed that users using this context are not supplier employees
        ),
        employeeRole: null,
        hidePlaceholderImages:
          chatDynamicConfig.hide_placeholder_images ??
          supplierDynamicConfig?.hide_placeholder_images ??
          false,
        largeScreenModalHoldout:
          chatDynamicConfig.large_screen_modal_holdout ??
          supplierDynamicConfig?.large_screen_modal_holdout ??
          false,
        name: normalizeQuotes(ec.restaurant_name!),
        onDemandPaymentStatementEnabled: false,
        onHold: false,
        orderReviewRequired: false,
        paymentsOnlyEnabled: false,
        payUponOrdering: false,
        requirePaymentMethodOnOrderPlacement: false,
        salesRepGrossProfitEnabled: false,
        salesRepIgnoreNegativeInGrossProfitTotal: false,
        salesRepModeEnabled: false,
        salesRepPeakPriceProtection: false,
        salesRepPriceFloorMode: null,
        salesRepPricingEnabled: false,
        showChatbot: false,
        showCreditRequest: false,
        showCrmActivities: false,
        showMenuCosting: false,
        showMenuOrderGuideGeneration: false,
        showOfflineMode: false,
        showPaymentLinks: false,
        showPaymentsTab: false,
        showProspectFinder: false,
        showTotalSalesDashboard: false,
        smartOrderEnabled:
          chatDynamicConfig.smart_order_enabled ??
          supplierDynamicConfig?.smart_order_enabled ??
          false,
        // Not shown in smart order (single supplier, non-supplier user)
        supplierName: '',
        supplierPaymentsStatus: PaymentsStatus.Disabled,
        supplierUUID: ec.supplier_uuid!,
        tagPrintingEnabled: false,
        timezone: ec.restaurant_timezone!,
        transactionFeeName: null,
        uuid: ec.restaurant_uuid!,
      };

      // Select the restaurant relationship that matches smart order params
      if (ec.restaurant_uuid === smartOrder.restaurant_uuid) {
        selectedRestaurantRelationship = restaurantRelationship;
      }

      restaurantRelationships.push(restaurantRelationship);
      restaurantUUIDToRelationshipMap[restaurantRelationship.uuid] =
        restaurantRelationship;
    }

    let context: UserContextType = {
      authStatus: AuthStatus.NotAuthenticated,
      checkAuthStatus,
      isGuestMode: false,
      previousAuthStatus: null,
      refetch: () => {},
    };

    if (!selectedRestaurantRelationship) {
      return {
        ...context,
        authStatus: AuthStatus.NotAuthorized,
        employeeCan: () => false,
        user,
      };
    }

    // Default moment to the selected restaurant's timezone globally
    moment.tz.setDefault(selectedRestaurantRelationship.timezone);

    const translationsEnabled =
      data?.whitelabel_config?.[0]?.translations_enabled ?? false;

    return {
      ...context,
      authStatus: AuthStatus.Authenticated,
      canCreateAccount: false,
      chatRestaurantMap: {},
      commissionSettings: null,
      creditRequestSettings: null,
      employeeCan: () => false,
      orderUUID: smartOrder.order_uuid,
      overrideChatStatus: () => {},
      restaurantRelationships,
      restaurantUUIDToRelationshipMap,
      selectedRestaurantRelationship,
      selectedRestaurantUUID: selectedRestaurantRelationship.uuid,
      selectRestaurant: (restaurantUUID: string) => {
        const restaurantRelationship = restaurantRelationships.find(
          (relationship) => relationship.uuid === restaurantUUID,
        );
        if (restaurantRelationship) {
          onSelectRestaurant(
            restaurantRelationship,
            supplierDynamicConfigMap[restaurantRelationship.supplierUUID],
            translationsEnabled,
          );
        }
      },
      smartOrderSettings: null,
      supplierDynamicConfigMap,
      supplierEmployeeMap: {},
      supplierEmployeeRole: null,
      supplierMap: {},
      translationsEnabled,
      updateUserLocally: () => {},
      user,
    };
  }, [
    authenticatedUserUUID,
    checkAuthStatus,
    data,
    loading,
    onSelectRestaurant,
    regenerateSmartOrdersLoading,
    smartOrderData,
    smartOrderLoading,
  ]);

  return (
    <UserSettingsContext.Provider
      value={SMART_ORDER_USER_SETTINGS_CONTEXT_VALUE}
    >
      <UserContext.Provider value={value}>{children}</UserContext.Provider>
    </UserSettingsContext.Provider>
  );
};
